import React, { useMemo, useState } from 'react';
import { Brain, Trash2 } from 'lucide-react';
import { useAuthStore } from '../../stores/authStore';
import { useMarkingStore } from '../../stores/markingStore';

const TASK_TYPES = [
  'Classwork', 'Homework', 'Test', 'Exam', 'Assignment',
  'Investigation', 'Project', 'Presentation', 'Practical Experiment', 'Orals', 'Other'
];

const TaskManager = ({ onClose }) => {
  const { user } = useAuthStore();
  const {
    markingTasks,
    markingQuestions,
    createMarkingTask,
    createMarkingQuestion,
    extractQuestionMetadata,
    deleteMarkingTask,
    deleteMarkingQuestion
  } = useMarkingStore();

  const [taskName, setTaskName] = useState('');
  const [taskType, setTaskType] = useState('Test');
  const [subject, setSubject] = useState('');
  const [grade, setGrade] = useState('');
  const [topicsInput, setTopicsInput] = useState('');
  const [selectedQuestionIds, setSelectedQuestionIds] = useState([]);

  const [qNumber, setQNumber] = useState('');
  const [qText, setQText] = useState('');
  const [qMarks, setQMarks] = useState('');
  const [qTopic, setQTopic] = useState('');
  const [qSubtopics, setQSubtopics] = useState('');
  const [qModelAnswer, setQModelAnswer] = useState('');
  const [qConcepts, setQConcepts] = useState([{ id: `c-${Date.now()}`, name: '', marks: '' }]);
  const [qSubquestions, setQSubquestions] = useState([{ id: `sq-${Date.now()}`, label: '', marks: '', modelAnswer: '' }]);
  const [visionImage, setVisionImage] = useState(null);
  const [visionGuidance, setVisionGuidance] = useState('');
  const [isExtracting, setIsExtracting] = useState(false);

  const [isSavingTask, setIsSavingTask] = useState(false);
  const [isSavingQuestion, setIsSavingQuestion] = useState(false);
  const [error, setError] = useState('');

  const sortedQuestions = useMemo(
    () => [...markingQuestions].sort((a, b) => String(a.questionNumber || '').localeCompare(String(b.questionNumber || ''))),
    [markingQuestions]
  );

  const resetQuestionForm = () => {
    setQNumber('');
    setQText('');
    setQMarks('');
    setQTopic('');
    setQSubtopics('');
    setQModelAnswer('');
    setQConcepts([{ id: `c-${Date.now()}`, name: '', marks: '' }]);
    setQSubquestions([{ id: `sq-${Date.now()}`, label: '', marks: '', modelAnswer: '' }]);
  };

  const toggleQuestionId = (questionId) => {
    setSelectedQuestionIds((prev) => (
      prev.includes(questionId)
        ? prev.filter((id) => id !== questionId)
        : [...prev, questionId]
    ));
  };

  const fileToBase64 = (file) => (
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const dataUrl = String(reader.result || '');
        const base64 = dataUrl.includes(',') ? dataUrl.split(',')[1] : dataUrl;
        resolve(base64);
      };
      reader.onerror = () => reject(new Error('Failed reading image.'));
      reader.readAsDataURL(file);
    })
  );

  const extractFromImage = async () => {
    if (!user?.uid) return;
    if (!visionImage) {
      setError('Choose an image first for AI autofill.');
      return;
    }
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(visionImage.type)) {
      setError('Use JPG, PNG, or WebP for AI autofill.');
      return;
    }
    if (visionImage.size > 8 * 1024 * 1024) {
      setError('Image is too large. Please use an image under 8MB.');
      return;
    }

    setError('');
    setIsExtracting(true);
    try {
      const imageBase64 = await fileToBase64(visionImage);
      const extracted = await extractQuestionMetadata({
        imageBase64,
        mimeType: visionImage.type,
        guidance: visionGuidance.trim()
      });
      if (!extracted) {
        throw new Error('No extraction output received.');
      }

      setQNumber((prev) => extracted.questionNumber || prev);
      setQText((prev) => extracted.questionText || prev);
      setQMarks((prev) => (Number.isFinite(Number(extracted.marks)) ? String(extracted.marks) : prev));
      setQTopic((prev) => extracted.topic || prev);
      setQSubtopics((prev) => (
        Array.isArray(extracted.subtopics) && extracted.subtopics.length > 0
          ? extracted.subtopics.join(', ')
          : prev
      ));
      setQModelAnswer((prev) => extracted.modelAnswer || prev);

      if (Array.isArray(extracted.concepts) && extracted.concepts.length > 0) {
        setQConcepts(
          extracted.concepts.map((c) => ({
            id: `c-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            name: c.name || '',
            marks: c.marks != null ? String(c.marks) : ''
          }))
        );
      }
      if (Array.isArray(extracted.subquestions) && extracted.subquestions.length > 0) {
        setQSubquestions(
          extracted.subquestions.map((sq) => ({
            id: `sq-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
            label: sq.label || '',
            marks: sq.marks != null ? String(sq.marks) : '',
            modelAnswer: sq.modelAnswer || ''
          }))
        );
      }
    } catch (err) {
      setError(err?.message || 'AI extraction failed.');
    } finally {
      setIsExtracting(false);
    }
  };

  const createQuestion = async () => {
    if (!user?.uid) return;
    if (!qNumber.trim()) {
      setError('Question number is required for new questions.');
      return;
    }
    setError('');
    setIsSavingQuestion(true);
    try {
      const payload = {
        userId: user.uid,
        questionNumber: qNumber.trim(),
        questionText: qText.trim(),
        subject: subject.trim(),
        grade: grade.trim(),
        marks: Number(qMarks) || 0,
        topic: qTopic.trim(),
        subtopics: qSubtopics.split(',').map((s) => s.trim()).filter(Boolean),
        modelAnswer: qModelAnswer.trim(),
        concepts: qConcepts
          .filter((c) => c.name.trim())
          .map((c) => ({ name: c.name.trim(), marks: Number(c.marks) || 0 })),
        subquestions: qSubquestions
          .filter((sq) => sq.label.trim())
          .map((sq) => ({
            label: sq.label.trim(),
            marks: Number(sq.marks) || 0,
            modelAnswer: (sq.modelAnswer || '').trim()
          }))
      };
      const created = await createMarkingQuestion(payload);
      setSelectedQuestionIds((prev) => [...new Set([...prev, created.id])]);
      resetQuestionForm();
    } catch (err) {
      setError(err?.message || 'Failed to create question.');
    } finally {
      setIsSavingQuestion(false);
    }
  };

  const createTask = async () => {
    if (!user?.uid) return;
    if (!taskName.trim()) {
      setError('Task name is required.');
      return;
    }
    if (selectedQuestionIds.length === 0) {
      setError('Select or create at least one question for the task.');
      return;
    }
    setError('');
    setIsSavingTask(true);
    try {
      await createMarkingTask({
        userId: user.uid,
        taskName: taskName.trim(),
        taskType,
        subject: subject.trim(),
        grade: grade.trim(),
        topics: topicsInput.split(',').map((t) => t.trim()).filter(Boolean),
        questionIds: selectedQuestionIds
      });
      setTaskName('');
      setTaskType('Test');
      setSubject('');
      setGrade('');
      setTopicsInput('');
      setSelectedQuestionIds([]);
    } catch (err) {
      setError(err?.message || 'Failed to create task.');
    } finally {
      setIsSavingTask(false);
    }
  };

  const sectionCardClass = 'rounded-xl border border-gray-300/90 bg-white p-5 shadow-[0_14px_30px_-18px_rgba(15,23,42,0.45)] space-y-4';
  const inputClass = 'w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 placeholder:text-gray-500 focus:border-sigma-500 focus:outline-none focus:ring-2 focus:ring-sigma-500/20';
  const compactInputClass = 'w-full rounded-lg border border-gray-300 bg-white px-2.5 py-2 text-sm text-gray-900 placeholder:text-gray-500 focus:border-sigma-500 focus:outline-none focus:ring-2 focus:ring-sigma-500/20';

  return (
    <div className="rounded-2xl border border-gray-300/90 bg-gray-50 p-6 space-y-6 shadow-[inset_0_2px_5px_rgba(0,0,0,0.3),0_0_10px_rgba(255,255,255,0.2)]">
      <div className="flex items-center justify-between border-b border-gray-200 pb-4">
        <div>
          <h3 className="text-xl font-semibold text-gray-900">Task Manager</h3>
          <p className="text-sm text-gray-700 mt-1">Create reusable questions, then bundle them into tasks.</p>
        </div>
        <button onClick={onClose} className="text-sm font-medium text-gray-700 hover:text-gray-900">Close</button>
      </div>

      <div className={sectionCardClass}>
        <h4 className="text-base font-semibold text-gray-900">1) Select Existing Questions</h4>
        {sortedQuestions.length === 0 ? (
          <p className="text-sm text-gray-600">No reusable questions yet. Create one below.</p>
        ) : (
          <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
            {sortedQuestions.map((q) => (
              <label key={q.id} className="flex items-start gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5 text-sm">
                <input
                  type="checkbox"
                  checked={selectedQuestionIds.includes(q.id)}
                  onChange={() => toggleQuestionId(q.id)}
                  className="mt-1 h-4 w-4"
                />
                <span className="text-gray-800 leading-5">
                  <span className="font-semibold text-gray-900">Q{q.questionNumber}</span>
                  {q.questionText ? ` - ${q.questionText}` : ''}
                  {q.marks != null ? ` (${q.marks})` : ''}
                </span>
              </label>
            ))}
          </div>
        )}
      </div>

      <div className={sectionCardClass}>
        <h4 className="text-base font-semibold text-gray-900">2) Create New Reusable Question</h4>
        <div className="rounded-xl border border-sigma-300 bg-sigma-50/60 p-4 space-y-3 shadow-[0_10px_24px_-18px_rgba(8,145,178,0.55)]">
          <p className="text-sm font-semibold text-sigma-900">AI Vision Autofill</p>
          <p className="text-sm text-sigma-800">
            Upload a question image and auto-fill this form, then edit any field before saving.
          </p>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(e) => setVisionImage(e.target.files?.[0] || null)}
            className={inputClass}
          />
          {visionImage && <p className="text-sm text-sigma-900">Selected: {visionImage.name}</p>}
          <textarea
            value={visionGuidance}
            onChange={(e) => setVisionGuidance(e.target.value)}
            placeholder="Optional guidance (e.g., 'use CAPS topic taxonomy', 'focus on subquestions')"
            rows={2}
            className={inputClass}
          />
          <button
            type="button"
            onClick={extractFromImage}
            disabled={isExtracting}
            className="inline-flex items-center rounded-lg bg-sigma-700 px-4 py-2 text-sm font-medium text-white hover:bg-sigma-800 disabled:opacity-60"
          >
            <Brain className="mr-1.5 h-4 w-4" />
            {isExtracting ? 'Extracting...' : 'Autofill from image'}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="space-y-1.5">
            <span className="text-sm font-medium text-gray-800">Question number</span>
            <input value={qNumber} onChange={(e) => setQNumber(e.target.value)} placeholder="e.g., 1.2" className={inputClass} />
          </label>
          <label className="space-y-1.5">
            <span className="text-sm font-medium text-gray-800">Marks</span>
            <input type="number" value={qMarks} onChange={(e) => setQMarks(e.target.value)} placeholder="Total marks" className={inputClass} />
          </label>
          <label className="space-y-1.5">
            <span className="text-sm font-medium text-gray-800">Topic</span>
            <input value={qTopic} onChange={(e) => setQTopic(e.target.value)} placeholder="Main topic" className={inputClass} />
          </label>
          <label className="space-y-1.5">
            <span className="text-sm font-medium text-gray-800">Subtopics</span>
            <input value={qSubtopics} onChange={(e) => setQSubtopics(e.target.value)} placeholder="Comma-separated subtopics" className={inputClass} />
          </label>
        </div>
        <label className="space-y-1.5 block">
          <span className="text-sm font-medium text-gray-800">Question text</span>
          <textarea value={qText} onChange={(e) => setQText(e.target.value)} placeholder="Question text (optional)" rows={3} className={inputClass} />
        </label>
        <label className="space-y-1.5 block">
          <span className="text-sm font-medium text-gray-800">Model answer</span>
          <textarea value={qModelAnswer} onChange={(e) => setQModelAnswer(e.target.value)} placeholder="Model answer for this question" rows={3} className={inputClass} />
        </label>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-800">Subquestions (with model answers)</p>
            <button
              type="button"
              className="rounded-md bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-200"
              onClick={() => setQSubquestions((prev) => [...prev, { id: `sq-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, label: '', marks: '', modelAnswer: '' }])}
            >
              Add subquestion
            </button>
          </div>
          {qSubquestions.map((sq) => (
            <div key={sq.id} className="grid grid-cols-1 md:grid-cols-4 gap-2 rounded-lg border border-gray-200 p-2.5 bg-gray-50">
              <input
                value={sq.label}
                onChange={(e) => setQSubquestions((prev) => prev.map((x) => x.id === sq.id ? { ...x, label: e.target.value } : x))}
                placeholder="Subquestion label (e.g., a)"
                className={compactInputClass}
              />
              <input
                type="number"
                value={sq.marks}
                onChange={(e) => setQSubquestions((prev) => prev.map((x) => x.id === sq.id ? { ...x, marks: e.target.value } : x))}
                placeholder="Marks"
                className={compactInputClass}
              />
              <input
                value={sq.modelAnswer}
                onChange={(e) => setQSubquestions((prev) => prev.map((x) => x.id === sq.id ? { ...x, modelAnswer: e.target.value } : x))}
                placeholder="Model answer"
                className={`${compactInputClass} md:col-span-2`}
              />
            </div>
          ))}
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-800">Concepts/Skills + Mark Allocation</p>
            <button
              type="button"
              className="rounded-md bg-gray-100 px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-200"
              onClick={() => setQConcepts((prev) => [...prev, { id: `c-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, name: '', marks: '' }])}
            >
              Add concept
            </button>
          </div>
          {qConcepts.map((c) => (
            <div key={c.id} className="grid grid-cols-1 md:grid-cols-3 gap-2 rounded-lg border border-gray-200 p-2.5 bg-gray-50">
              <input
                value={c.name}
                onChange={(e) => setQConcepts((prev) => prev.map((x) => x.id === c.id ? { ...x, name: e.target.value } : x))}
                placeholder="Concept/Skill"
                className={compactInputClass}
              />
              <input
                type="number"
                value={c.marks}
                onChange={(e) => setQConcepts((prev) => prev.map((x) => x.id === c.id ? { ...x, marks: e.target.value } : x))}
                placeholder="Marks"
                className={compactInputClass}
              />
              <button
                type="button"
                className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 text-left hover:bg-red-100"
                onClick={() => setQConcepts((prev) => prev.filter((x) => x.id !== c.id))}
              >
                Remove
              </button>
            </div>
          ))}
        </div>

        <button type="button" onClick={createQuestion} disabled={isSavingQuestion} className="rounded-lg bg-sigma-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-sigma-800 disabled:opacity-60">
          {isSavingQuestion ? 'Saving Question...' : 'Save Question to sigma-questions'}
        </button>
      </div>

      <div className={sectionCardClass}>
        <h4 className="text-base font-semibold text-gray-900">3) Create Task (stores questionIds only)</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <label className="space-y-1.5">
            <span className="text-sm font-medium text-gray-800">Task name</span>
            <input value={taskName} onChange={(e) => setTaskName(e.target.value)} placeholder="Task name" className={inputClass} />
          </label>
          <label className="space-y-1.5">
            <span className="text-sm font-medium text-gray-800">Task type</span>
            <select value={taskType} onChange={(e) => setTaskType(e.target.value)} className={inputClass}>
              {TASK_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label className="space-y-1.5">
            <span className="text-sm font-medium text-gray-800">Subject</span>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Subject" className={inputClass} />
          </label>
          <label className="space-y-1.5">
            <span className="text-sm font-medium text-gray-800">Grade</span>
            <input value={grade} onChange={(e) => setGrade(e.target.value)} placeholder="Grade" className={inputClass} />
          </label>
        </div>
        <label className="space-y-1.5 block">
          <span className="text-sm font-medium text-gray-800">Topics</span>
          <input value={topicsInput} onChange={(e) => setTopicsInput(e.target.value)} placeholder="Comma-separated topics" className={inputClass} />
        </label>
        <p className="text-sm text-gray-700">Selected question IDs: <span className="font-semibold text-gray-900">{selectedQuestionIds.length}</span></p>
        <button type="button" onClick={createTask} disabled={isSavingTask} className="rounded-lg bg-sigma-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-sigma-800 disabled:opacity-60">
          {isSavingTask ? 'Saving Task...' : 'Create Task in sigma-tasks'}
        </button>
      </div>

      {error && <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}

      <div className="border-t border-gray-200 pt-4 space-y-4">
        <div>
          <p className="text-sm font-semibold text-gray-900 mb-2">Existing Tasks</p>
          {markingTasks.length === 0 && <p className="text-sm text-gray-600">No tasks yet.</p>}
          {markingTasks.map((task) => (
            <div key={task.id} className="flex items-center justify-between border border-gray-200 bg-white rounded-lg px-3 py-2.5 mb-2">
              <div className="text-sm">
                <div className="font-semibold text-gray-900">{task.taskName}</div>
                <div className="text-gray-700">{task.taskType} | Q IDs: {(task.questionIds || []).length}</div>
              </div>
              <button type="button" onClick={() => deleteMarkingTask(task.id)} className="text-gray-500 hover:text-red-700">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>

        <div>
          <p className="text-sm font-semibold text-gray-900 mb-2">Existing Questions</p>
          {markingQuestions.length === 0 && <p className="text-sm text-gray-600">No questions yet.</p>}
          {markingQuestions.map((q) => (
            <div key={q.id} className="flex items-center justify-between border border-gray-200 bg-white rounded-lg px-3 py-2.5 mb-2">
              <div className="text-sm">
                <div className="font-semibold text-gray-900">Q{q.questionNumber} ({q.marks || 0})</div>
                <div className="text-gray-700 truncate max-w-[520px]">{q.questionText || q.topic || 'No text'}</div>
              </div>
              <button type="button" onClick={() => deleteMarkingQuestion(q.id)} className="text-gray-500 hover:text-red-700">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TaskManager;
